/**
 * Hook to detect which domain the app is running on.
 *
 * Used for domain-based routing:
 *   - app.kyykka.fi  → Spectator app (public)
 *   - tuho.kyykka.fi → Staff interface (judge + admin)
 *
 * In development (localhost), defaults to 'tuho' for staff features.
 * Can be overridden with ?domain=app or ?domain=tuho query param.
 */

export type AppDomain = 'app' | 'tuho';

/**
 * Detects the current domain from hostname or query params.
 */
export function getDomain(): AppDomain {
  const hostname = window.location.hostname;
  const searchParams = new URLSearchParams(window.location.search);

  // Allow override via query param for development/testing
  const domainOverride = searchParams.get('domain');
  if (domainOverride === 'app' || domainOverride === 'tuho') {
    return domainOverride;
  }

  // Production domain detection
  if (hostname.includes('app.kyykka.fi')) {
    return 'app';
  }
  if (hostname.includes('tuho.kyykka.fi')) {
    return 'tuho';
  }

  // Development: default to tuho for staff features
  // Use ?domain=app to test spectator app locally
  return 'tuho';
}

/**
 * Hook to get current domain.
 */
export function useDomain(): AppDomain {
  return getDomain();
}

/**
 * Check if running on spectator app domain.
 */
export function isAppDomain(): boolean {
  return getDomain() === 'app';
}

/**
 * Check if running on staff domain.
 */
export function isTuhoDomain(): boolean {
  return getDomain() === 'tuho';
}

